import React, { useState, useEffect } from 'react'
import { X, Video, Sliders } from 'lucide-react'

/**
 * Video Export Dialog Component
 * Allows users to configure FPS and resolution/scaling for video export
 */
const VideoExportDialog = ({ isOpen, onClose, onConfirm, currentWidth, currentHeight }) => {
  const [selectedFps, setSelectedFps] = useState('Smooth') // Default to Smooth (30fps)
  const [resolutionMode, setResolutionMode] = useState('standard') // 'standard' or 'custom'
  const [selectedResolution, setSelectedResolution] = useState(null)
  const [scalePercentage, setScalePercentage] = useState(100)

  // FPS presets
  const fpsPresets = [
    { name: 'Draft', fps: 1, description: 'Fastest rendering' },
    { name: 'Basic', fps: 5, description: 'Basic quality' },
    { name: 'Classic', fps: 24, description: 'Cinematic feel' },
    { name: 'Smooth', fps: 30, description: 'Recommended' },
    { name: 'Ultra HD', fps: 60, description: 'Highest quality' },
  ]

  // Standard resolutions
  const standardResolutions = [
    { name: '240p', width: 426, height: 240 },
    { name: '360p', width: 640, height: 360 },
    { name: '480p', width: 854, height: 480 },
    { name: '720p (HD)', width: 1280, height: 720 },
    { name: '1080p (Full HD)', width: 1920, height: 1080 },
    { name: '1440p (2K)', width: 2560, height: 1440 },
    { name: '2160p (4K)', width: 3840, height: 2160 },
  ]

  // Determine if current dimensions match a standard resolution
  useEffect(() => {
    if (!currentWidth || !currentHeight) return

    // Check if current dimensions match a standard resolution
    const matchingRes = standardResolutions.find(
      res => res.width === currentWidth && res.height === currentHeight
    )

    if (matchingRes) {
      setResolutionMode('standard')
      setSelectedResolution(matchingRes.name)
    } else {
      // Check if it's a common aspect ratio match (e.g., 720x1280 vs 1280x720)
      const portraitMatch = standardResolutions.find(
        res => res.width === currentHeight && res.height === currentWidth
      )
      
      if (portraitMatch) {
        setResolutionMode('standard')
        setSelectedResolution(portraitMatch.name)
      } else {
        setResolutionMode('custom')
        setScalePercentage(100)
      }
    }
  }, [currentWidth, currentHeight])

  // Calculate final dimensions based on mode
  const getFinalDimensions = () => {
    if (resolutionMode === 'standard' && selectedResolution) {
      const res = standardResolutions.find(r => r.name === selectedResolution)
      if (res) {
        // Check if current is portrait (height > width)
        const isPortrait = currentHeight > currentWidth
        return {
          width: isPortrait ? res.height : res.width,
          height: isPortrait ? res.width : res.height,
        }
      }
    } else if (resolutionMode === 'custom') {
      return {
        width: Math.round(currentWidth * (scalePercentage / 100)),
        height: Math.round(currentHeight * (scalePercentage / 100)),
      }
    }
    
    // Fallback to current dimensions
    return {
      width: currentWidth || 720,
      height: currentHeight || 1280,
    }
  }

  const handleConfirm = () => {
    const { width, height } = getFinalDimensions()
    
    // Get FPS value from selected preset
    const fpsPreset = fpsPresets.find(p => p.name === selectedFps)
    const fpsValue = fpsPreset ? fpsPreset.fps : 30
    
    onConfirm({
      req_fps: fpsValue,
      req_width: width,
      req_height: height,
    })
    
    onClose()
  }

  if (!isOpen) return null

  const finalDims = getFinalDimensions()

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Video className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Export Video</h2>
              <p className="text-sm text-gray-500">Configure video export settings</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* FPS Preset Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Frame Rate Quality
            </label>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {fpsPresets.map((preset) => {
                const isSelected = selectedFps === preset.name
                return (
                  <button
                    key={preset.name}
                    type="button"
                    onClick={() => setSelectedFps(preset.name)}
                    className={`p-3 rounded-lg border-2 transition-all text-left ${
                      isSelected
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div className="font-medium text-sm text-gray-900">{preset.name}</div>
                    <div className="text-xs text-gray-600 mt-1">
                      {preset.fps} FPS
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {preset.description}
                    </div>
                  </button>
                )
              })}
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Selected: <span className="font-medium">{fpsPresets.find(p => p.name === selectedFps)?.fps || 30} FPS</span>
            </p>
          </div>

          {/* Resolution Mode Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Resolution
            </label>
            
            {/* Current Dimensions Info */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                Current dimensions: <span className="font-medium">{currentWidth} × {currentHeight}</span>
              </p>
            </div>

            {/* Mode Toggle */}
            <div className="flex space-x-4 mb-4">
              <button
                type="button"
                onClick={() => setResolutionMode('standard')}
                className={`flex-1 px-4 py-2 rounded-lg border-2 transition-all ${
                  resolutionMode === 'standard'
                    ? 'border-purple-500 bg-purple-50 text-purple-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                }`}
              >
                Standard Resolution
              </button>
              <button
                type="button"
                onClick={() => setResolutionMode('custom')}
                className={`flex-1 px-4 py-2 rounded-lg border-2 transition-all ${
                  resolutionMode === 'custom'
                    ? 'border-purple-500 bg-purple-50 text-purple-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                }`}
              >
                Custom Scale
              </button>
            </div>

            {/* Standard Resolution Options */}
            {resolutionMode === 'standard' && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {standardResolutions.map((res) => {
                  // Check if current is portrait and adjust display
                  const isPortrait = currentHeight > currentWidth
                  const displayWidth = isPortrait ? res.height : res.width
                  const displayHeight = isPortrait ? res.width : res.height
                  const isSelected = selectedResolution === res.name

                  return (
                    <button
                      key={res.name}
                      type="button"
                      onClick={() => setSelectedResolution(res.name)}
                      className={`p-3 rounded-lg border-2 transition-all text-left ${
                        isSelected
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <div className="font-medium text-sm text-gray-900">{res.name}</div>
                      <div className="text-xs text-gray-600 mt-1">
                        {displayWidth} × {displayHeight}
                      </div>
                    </button>
                  )
                })}
              </div>
            )}

            {/* Custom Scale Percentage */}
            {resolutionMode === 'custom' && (
              <div>
                <div className="flex items-center space-x-4 mb-2">
                  <input
                    type="number"
                    min="10"
                    max="500"
                    value={scalePercentage}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 100
                      setScalePercentage(Math.max(10, Math.min(500, value)))
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <span className="text-gray-600 font-medium">%</span>
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => setScalePercentage(Math.max(10, scalePercentage - 10))}
                      className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded hover:bg-gray-50"
                    >
                      -
                    </button>
                    <button
                      type="button"
                      onClick={() => setScalePercentage(Math.min(500, scalePercentage + 10))}
                      className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded hover:bg-gray-50"
                    >
                      +
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mb-3">Range: 10% - 500%</p>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">
                    Output: <span className="font-medium">
                      {Math.round(currentWidth * (scalePercentage / 100))} × {Math.round(currentHeight * (scalePercentage / 100))}
                    </span>
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Preview Summary */}
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h3 className="text-sm font-medium text-blue-900 mb-2">Export Summary</h3>
            <div className="space-y-1 text-sm text-blue-800">
              <p>Resolution: <span className="font-medium">{finalDims.width} × {finalDims.height}</span></p>
              <p>Frame Rate: <span className="font-medium">{fpsPresets.find(p => p.name === selectedFps)?.fps || 30} FPS ({selectedFps})</span></p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2"
          >
            <Video className="w-4 h-4" />
            <span>Export Video</span>
          </button>
        </div>
      </div>
    </div>
  )
}

export default VideoExportDialog

